import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { galleryApi } from '../../api/api';
import { youtubeThumb } from '../../utils/youtube';
import useHistoryBackClose from '../../hooks/useHistoryBackClose';
import {
  AdminSelect,
  AdminFieldInput,
  AdminFieldSelect,
  AdminModalClose,
  AdminCheckbox,
} from '../components/AdminUI';

const CATEGORIES = ['temple', 'satsang', 'guruji', 'meditation', 'festivals', 'events'];
const CATEGORY_OPTIONS = CATEGORIES.map((c) => ({
  value: c,
  label: c.charAt(0).toUpperCase() + c.slice(1),
}));
const PAGE_SIZE_OPTIONS = [12, 24, 36, 48];
const WEB_FILTERS = [
  { val: '', label: 'All' },
  { val: 'temple', label: 'Temple' },
  { val: 'festivals', label: 'Festivals' },
  { val: 'events', label: 'Events' },
];

const EMPTY_FORM = {
  title: '',
  src: '',
  category: 'temple',
  isVideo: false,
  youtubeId: '',
  duration: '',
  className: 'aspect-square',
  featured: false,
};

const pad2 = (n) => String(Math.max(0, parseInt(n, 10) || 0)).padStart(2, '0');

const parseDuration = (str) => {
  const parts = String(str || '').split(':').map((p) => parseInt(p, 10) || 0);
  if (parts.length === 3) return { h: parts[0], m: parts[1], s: parts[2] };
  if (parts.length === 2) return { h: 0, m: parts[0], s: parts[1] };
  if (parts.length === 1 && parts[0]) return { h: 0, m: parts[0], s: 0 };
  return { h: 0, m: 0, s: 0 };
};

const formatDuration = (h, m, s) => {
  const hh = parseInt(h, 10) || 0;
  const mm = Math.min(59, parseInt(m, 10) || 0);
  const ss = Math.min(59, parseInt(s, 10) || 0);
  if (hh > 0) return `${hh}:${pad2(mm)}:${pad2(ss)}`;
  return `${mm}:${pad2(ss)}`;
};

const DurationPicker = ({ label, value, onChange }) => {
  const parsed = parseDuration(value);
  const [focusKey, setFocusKey] = useState(null);
  const [draft, setDraft] = useState('');

  const commit = (key, raw) => {
    const n = raw === '' ? 0 : Math.max(0, parseInt(String(raw).replace(/^0+(?=\d)/, ''), 10) || 0);
    const next = {
      h: key === 'h' ? Math.min(99, n) : parsed.h,
      m: key === 'm' ? Math.min(59, n) : parsed.m,
      s: key === 's' ? Math.min(59, n) : parsed.s,
    };
    onChange(formatDuration(next.h, next.m, next.s));
  };

  const display = (key) => (focusKey === key ? draft : String(parsed[key]));
  const fieldCls = 'admin-field-input text-center';

  const renderField = (k, sub) => (
    <div key={k}>
      <input
        type="search"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={2}
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        value={display(k)}
        className={fieldCls}
        onFocus={(e) => {
          setFocusKey(k);
          setDraft(String(parsed[k]));
          requestAnimationFrame(() => e.target.select());
        }}
        onBlur={() => {
          commit(k, draft);
          setFocusKey(null);
        }}
        onChange={(e) => {
          const cleaned = e.target.value.replace(/\D/g, '').slice(0, 2).replace(/^0+(?=\d)/, '');
          setDraft(cleaned);
          commit(k, cleaned === '' ? '0' : cleaned);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.target.blur();
        }}
      />
      <p className="text-center text-xs text-gray-400 mt-0.5">{sub}</p>
    </div>
  );

  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1.5">{label}</label>
      <div className="grid grid-cols-3 gap-2">
        {renderField('h', 'Hrs')}
        {renderField('m', 'Min')}
        {renderField('s', 'Sec')}
      </div>
    </div>
  );
};

const Input = ({ label, required, clearable, onClear, ...props }) => (
  <AdminFieldInput label={label} required={required} clearable={clearable} onClear={onClear} {...props} />
);

/** Simple media tile — matches public Gallery page photo/video UI */
const GalleryAdminCard = ({ item, onPreview, onEdit, onDelete }) => {
  const isVideo = !!(item.isVideo === true || item.isVideo === 1 || item.isVideo === '1');
  const thumb = item.src || youtubeThumb(item.youtubeId) || '/logo.jpg';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onPreview}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onPreview();
        }
      }}
      className="gallery-item group relative overflow-hidden rounded-lg aspect-square bg-gray-100 cursor-pointer"
    >
      <img
        src={thumb}
        alt={item.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        onError={(e) => { e.target.src = '/logo.jpg'; }}
      />

      {isVideo ? (
        <>
          <div className="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center pointer-events-none">
            <i className="fas fa-play-circle text-white text-4xl opacity-80 group-hover:opacity-100 transition-opacity duration-300"></i>
          </div>
          {item.duration && (
            <div className="absolute bottom-2 right-2 pointer-events-none">
              <span className="bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs">{item.duration}</span>
            </div>
          )}
        </>
      ) : (
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center pointer-events-none">
          <i className="fas fa-search-plus text-white text-xl md:text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
        </div>
      )}

      {!!(item.featured === true || item.featured === 1 || item.featured === '1') && (
        <div className="absolute top-2 left-2 z-10 pointer-events-none">
          <span className="inline-flex items-center gap-1 bg-orange-500 text-white text-xs font-semibold px-2 py-1 rounded-md shadow">
            <i className="fas fa-star text-xs"></i> Featured
          </span>
        </div>
      )}

      {/* Edit / Delete — always reachable */}
      <div className="absolute top-2 right-2 flex gap-1.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onEdit}
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: '#eff6ff', color: '#2563eb' }}
          title="Edit"
        >
          <i className="fas fa-edit text-xs"></i>
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: '#fef2f2', color: '#dc2626' }}
          title="Delete"
        >
          <i className="fas fa-trash text-xs"></i>
        </button>
      </div>
    </div>
  );
};

const GalleryModal = ({ mode, initial, onSave, onClose, saving }) => {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [mediaTab, setMediaTab] = useState('url'); // 'url' | 'file' | 'youtube'
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(initial?.src || '');
  const [formError, setFormError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    const next = initial || EMPTY_FORM;
    setForm(next);
    setPreview(next.src || '');
    setSelectedFile(null);
    const isVideo = !!(next.isVideo === true || next.isVideo === 1 || next.isVideo === '1');
    setMediaTab(isVideo ? 'youtube' : 'url');
  }, [initial]);

  const set = (field, val) => {
    setForm((prev) => ({ ...prev, [field]: val }));
    setFormError('');
  };

  const switchTab = (tab) => {
    setMediaTab(tab);
    setFormError('');
    setForm((prev) => ({
      ...prev,
      isVideo: tab === 'youtube',
      youtubeId: tab === 'youtube' ? prev.youtubeId : '',
      duration: tab === 'youtube' ? prev.duration : '',
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setFormError('Title is required'); return; }

    const isVideo = mediaTab === 'youtube';
    if (isVideo && !(form.youtubeId || '').trim()) {
      setFormError('YouTube Video ID is required');
      return;
    }
    if (mediaTab === 'url' && !(form.src || '').trim()) {
      setFormError('Image URL is required');
      return;
    }
    if (mediaTab === 'file' && !selectedFile && !(form.src || '').trim()) {
      setFormError('Please select an image file');
      return;
    }

    const payload = {
      ...form,
      isVideo,
      featured: !!form.featured,
      youtubeId: isVideo ? form.youtubeId : null,
      duration: isVideo ? form.duration : null,
      className: 'aspect-square',
    };

    if (mediaTab === 'file' && selectedFile) {
      const fd = new FormData();
      fd.append('image', selectedFile);
      fd.append('title', payload.title);
      fd.append('category', payload.category);
      fd.append('isVideo', '0');
      fd.append('youtubeId', '');
      fd.append('duration', '');
      fd.append('className', 'aspect-square');
      fd.append('featured', payload.featured ? '1' : '0');
      onSave(fd, true);
    } else {
      onSave(payload, false);
    }
  };

  const thumbPreview = preview
    || (mediaTab === 'youtube' ? youtubeThumb(form.youtubeId) : '');

  const tabBtn = (tab, icon, label) => (
    <button
      type="button"
      onClick={() => switchTab(tab)}
      className={`flex-1 py-2 text-center transition-colors ${mediaTab === tab ? 'admin-chip-active' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
    >
      <i className={`fas ${icon} mr-1`}></i>{label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-screen overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-800 text-lg">
            {mode === 'edit' ? 'Edit Gallery Item' : 'Add Gallery Item'}
          </h3>
          <AdminModalClose onClick={onClose} />
        </div>

        <form onSubmit={handleSubmit} autoComplete="off" className="p-6 space-y-4">
          {formError && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              <i className="fas fa-exclamation-circle mr-2"></i>{formError}
            </div>
          )}

          <Input
            label="Title"
            required
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
            onClear={() => set('title', '')}
            clearable
            placeholder="e.g. Temple Architecture"
          />

          <AdminFieldSelect
            label="Category"
            value={form.category}
            onChange={(v) => set('category', v)}
            options={CATEGORY_OPTIONS}
            aria-label="Gallery category"
          />

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Media</label>
            <div className="flex rounded-xl border border-gray-200 overflow-hidden text-sm font-medium">
              {tabBtn('url', 'fa-link', 'URL')}
              {tabBtn('file', 'fa-upload', 'Upload')}
              {tabBtn('youtube', 'fa-play-circle', 'YouTube')}
            </div>
          </div>

          {mediaTab === 'url' && (
            <Input
              label="Image URL"
              required
              value={form.src || ''}
              onChange={(e) => { set('src', e.target.value); setPreview(e.target.value); }}
              onClear={() => { set('src', ''); setPreview(''); }}
              clearable
              placeholder="https://..."
            />
          )}

          {mediaTab === 'file' && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Image File <span className="text-red-500">*</span></label>
              <div
                onClick={() => fileInputRef.current.click()}
                className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-orange-400 transition-colors bg-gray-50"
              >
                {selectedFile ? (
                  <p className="text-sm text-green-600 font-medium"><i className="fas fa-check-circle mr-2"></i>{selectedFile.name}</p>
                ) : (
                  <>
                    <i className="fas fa-cloud-upload-alt text-gray-400 text-2xl mb-2"></i>
                    <p className="text-sm text-gray-500">Click to select image</p>
                    <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP up to 10 MB</p>
                  </>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
            </div>
          )}

          {mediaTab === 'youtube' && (
            <div className="space-y-3">
              <Input
                label="YouTube Video ID"
                required
                value={form.youtubeId || ''}
                onChange={(e) => set('youtubeId', e.target.value)}
                onClear={() => set('youtubeId', '')}
                clearable
                placeholder="e.g. AGjx27_87iY"
              />
              <DurationPicker label="Duration" value={form.duration || ''} onChange={(v) => set('duration', v)} />
              <Input
                label="Thumbnail URL (optional)"
                value={form.src || ''}
                onChange={(e) => { set('src', e.target.value); setPreview(e.target.value); }}
                onClear={() => { set('src', ''); setPreview(''); }}
                clearable
                placeholder="Leave blank to use YouTube thumbnail"
              />
            </div>
          )}

          {thumbPreview && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Preview</label>
              <img src={thumbPreview} alt="preview" className="w-full aspect-video object-cover rounded-xl border border-gray-200" onError={() => setPreview('')} />
            </div>
          )}

          <AdminCheckbox
            checked={!!form.featured}
            onChange={(v) => set('featured', v)}
            label="Mark as featured"
          />

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 px-4 border border-gray-200 rounded-xl text-gray-600 font-semibold text-sm hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 px-4 admin-btn-primary text-sm disabled:opacity-60">
              {saving ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : <><i className="fas fa-save"></i> Save</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ConfirmDialog = ({ title, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" onClick={onCancel}>
    <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
      <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: '#fee2e2' }}>
        <i className="fas fa-trash text-xl" style={{ color: '#ef4444' }}></i>
      </div>
      <h4 className="font-bold text-gray-800 mb-1">Delete Item?</h4>
      <p className="text-sm text-gray-500 mb-4">"{title}" will be permanently removed.</p>
      <div className="flex gap-3">
        <button type="button" onClick={onCancel} className="flex-1 py-2 border border-gray-200 rounded-xl text-gray-600 font-semibold text-sm">Cancel</button>
        <button type="button" onClick={onConfirm} disabled={loading}
          className="flex-1 py-2 admin-btn-danger rounded-xl font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2">
          {loading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-trash"></i>} Delete
        </button>
      </div>
    </div>
  </div>
);

const GalleryAdmin = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [mediaFilter, setMediaFilter] = useState(''); // '' | 'image' | 'video'
  const [featuredFilter, setFeaturedFilter] = useState(''); // '' | 'featured' | 'other'
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(12);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [playingVideo, setPlayingVideo] = useState(null);

  const closeLightbox = useCallback(() => setLightboxImage(null), []);
  const closeVideo = useCallback(() => setPlayingVideo(null), []);
  useHistoryBackClose(!!lightboxImage, closeLightbox, 'adminGalleryLightbox');
  useHistoryBackClose(!!playingVideo, closeVideo, 'adminGalleryVideo');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await galleryApi.getAll();
      if (res.success) setItems(res.data || []);
      else setError(res.message);
    } catch { setError('Failed to load gallery'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setCurrentPage(1); }, [search, catFilter, mediaFilter, featuredFilter, perPage]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      const isVideo = !!(item.isVideo === true || item.isVideo === 1 || item.isVideo === '1');
      const isFeatured = !!(item.featured === true || item.featured === 1 || item.featured === '1');
      if (catFilter && item.category !== catFilter) return false;
      if (mediaFilter === 'video' && !isVideo) return false;
      if (mediaFilter === 'image' && isVideo) return false;
      if (featuredFilter === 'featured' && !isFeatured) return false;
      if (featuredFilter === 'other' && isFeatured) return false;
      if (!q) return true;
      return (
        (item.title || '').toLowerCase().includes(q) ||
        (item.category || '').toLowerCase().includes(q) ||
        (item.youtubeId || '').toLowerCase().includes(q)
      );
    });
  }, [items, search, catFilter, mediaFilter, featuredFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / perPage));
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredItems.slice(start, start + perPage);
  }, [filteredItems, currentPage, perPage]);

  const rangeStart = filteredItems.length === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const rangeEnd = Math.min(currentPage * perPage, filteredItems.length);

  const handleSave = async (formOrFD, isFormData) => {
    setSaving(true);
    try {
      let res;
      if (modal.mode === 'edit') {
        res = await galleryApi.update(modal.item.id, formOrFD);
      } else {
        res = isFormData ? await galleryApi.create(formOrFD) : await galleryApi.createJson(formOrFD);
      }
      if (!res.success) throw new Error(res.message);
      setModal(null);
      showToast(modal.mode === 'edit' ? '✅ Item updated!' : '✅ Item added!');
      load();
    } catch (err) { alert(err.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await galleryApi.delete(deleteTarget.id);
      if (!res.success) throw new Error(res.message);
      setDeleteTarget(null);
      showToast('🗑️ Item deleted');
      load();
    } catch (err) { alert(err.message || 'Delete failed'); }
    finally { setDeleting(false); }
  };

  const isVideoItem = (item) => !!(item.isVideo === true || item.isVideo === 1 || item.isVideo === '1');

  const handleItemPreview = (item, index) => {
    if (isVideoItem(item) && item.youtubeId) {
      setPlayingVideo(item);
      return;
    }
    setLightboxImage(item);
    setLightboxIndex(index);
  };

  const photoItems = useMemo(
    () => paginatedItems.filter((item) => !isVideoItem(item)),
    [paginatedItems]
  );

  const nextImage = () => {
    if (!photoItems.length) return;
    const idx = photoItems.findIndex((p) => p.id === lightboxImage?.id);
    const nextIdx = (idx + 1) % photoItems.length;
    setLightboxIndex(nextIdx);
    setLightboxImage(photoItems[nextIdx]);
  };

  const prevImage = () => {
    if (!photoItems.length) return;
    const idx = photoItems.findIndex((p) => p.id === lightboxImage?.id);
    const prevIdx = (idx - 1 + photoItems.length) % photoItems.length;
    setLightboxIndex(prevIdx);
    setLightboxImage(photoItems[prevIdx]);
  };

  return (
    <div className="max-w-7xl mx-auto">
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-gray-800 text-white px-5 py-3 rounded-2xl shadow-2xl text-sm font-medium">{toast}</div>
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">🖼️ Gallery</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading ? 'Loading…' : `${filteredItems.length} total · showing ${rangeStart}–${rangeEnd}`}
          </p>
        </div>
        <button type="button" onClick={() => setModal({ mode: 'add', item: { ...EMPTY_FORM } })} className="admin-btn-primary px-5 py-2.5 text-sm">
          <i className="fas fa-plus"></i> Add Item
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3 mb-4">
        <div className="flex flex-col md:flex-row gap-2 md:items-center">
          <div className="admin-search-wrap flex-1 min-w-0">
            <i className="fas fa-search admin-search-icon" aria-hidden="true"></i>
            <input
              type="search"
              inputMode="search"
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title, category or YouTube ID..."
              className="admin-search-input"
            />
            {search && (
              <button
                type="button"
                className="admin-search-clear"
                onClick={() => setSearch('')}
                aria-label="Clear search"
              >
                <i className="fas fa-times text-xs"></i>
              </button>
            )}
          </div>
          <AdminSelect
            aria-label="Filter by media"
            value={mediaFilter}
            onChange={setMediaFilter}
            options={[
              { value: '', label: 'All Media' },
              { value: 'image', label: 'Photos' },
              { value: 'video', label: 'Videos' },
            ]}
          />
          <AdminSelect
            aria-label="Filter by featured"
            value={featuredFilter}
            onChange={setFeaturedFilter}
            options={[
              { value: '', label: 'All Items' },
              { value: 'featured', label: 'Featured' },
              { value: 'other', label: 'Not Featured' },
            ]}
          />
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-gray-500 whitespace-nowrap">Per page</span>
            <AdminSelect
              aria-label="Items per page"
              value={perPage}
              onChange={setPerPage}
              options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: String(n) }))}
            />
          </div>
        </div>
      </div>

      {/* Category chips — same as public Gallery page */}
      <div className="overflow-x-auto scrollbar-hide mb-5">
        <div className="flex flex-nowrap gap-2">
          {WEB_FILTERS.map((f) => (
            <button
              key={f.val || 'all'}
              type="button"
              onClick={() => setCatFilter(f.val)}
              className={`flex-shrink-0 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                catFilter === f.val
                  ? 'admin-chip-active'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-orange-300'
              }`}
            >
              {f.label}
            </button>
          ))}
          {CATEGORIES.filter((c) => !['temple', 'festivals', 'events'].includes(c)).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCatFilter(c)}
              className={`flex-shrink-0 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors border capitalize ${
                catFilter === c
                  ? 'admin-chip-active'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-orange-300'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <i className="fas fa-exclamation-circle mr-2"></i>{error}
          <button type="button" onClick={load} className="ml-3 underline">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center">
          <i className="fas fa-spinner fa-spin text-2xl" style={{ color: '#f97316' }}></i>
          <p className="text-gray-500 text-sm mt-3">Loading gallery...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="py-20 text-center bg-white rounded-2xl border border-dashed border-gray-200">
          <i className="fas fa-images text-4xl mb-3" style={{ color: '#d1d5db' }}></i>
          <p className="text-gray-500 font-medium">No gallery items found</p>
          <button type="button" onClick={() => setModal({ mode: 'add', item: { ...EMPTY_FORM } })} className="mt-4 admin-btn-primary px-5 py-2.5 text-sm">
            <i className="fas fa-plus mr-2"></i>Add Item
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {paginatedItems.map((item, idx) => (
              <GalleryAdminCard
                key={item.id}
                item={item}
                onPreview={() => handleItemPreview(item, idx)}
                onEdit={() => setModal({ mode: 'edit', item })}
                onDelete={() => setDeleteTarget(item)}
              />
            ))}
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-xs text-gray-500 leading-none">
              Page {currentPage} of {totalPages} · {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}
            </p>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className={`px-3 py-2 rounded-lg text-sm font-medium ${currentPage === 1 ? 'opacity-40 cursor-not-allowed bg-gray-100 text-gray-400' : 'bg-white border border-gray-200 text-gray-700 hover:border-orange-400'}`}
              >
                <i className="fas fa-chevron-left mr-1"></i>Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => totalPages <= 7 || p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) => (
                  p === '…' ? (
                    <span key={`e${idx}`} className="px-1 text-gray-400 text-sm">…</span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setCurrentPage(p)}
                      className="w-9 h-9 rounded-lg text-sm font-semibold"
                      style={p === currentPage
                        ? { background: 'linear-gradient(to right, #f97316, #dc2626)', color: '#fff' }
                        : { background: '#fff', color: '#4b5563', border: '1px solid #e5e7eb' }}
                    >
                      {p}
                    </button>
                  )
                ))}
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className={`px-3 py-2 rounded-lg text-sm font-medium ${currentPage === totalPages ? 'opacity-40 cursor-not-allowed bg-gray-100 text-gray-400' : 'bg-white border border-gray-200 text-gray-700 hover:border-orange-400'}`}
              >
                Next<i className="fas fa-chevron-right ml-1"></i>
              </button>
            </div>
          </div>
        </>
      )}

      {modal && (
        <GalleryModal
          mode={modal.mode}
          initial={modal.item ? {
            title: modal.item.title || '',
            src: modal.item.src || '',
            category: modal.item.category || 'temple',
            isVideo: !!modal.item.isVideo,
            youtubeId: modal.item.youtubeId || '',
            duration: modal.item.duration || '',
            className: modal.item.className || 'aspect-square',
            featured: !!(modal.item.featured === true || modal.item.featured === 1 || modal.item.featured === '1'),
          } : EMPTY_FORM}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
        />
      )}
      {deleteTarget && (
        <ConfirmDialog
          title={deleteTarget.title}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          loading={deleting}
        />
      )}

      {/* Photo lightbox — same as public Gallery */}
      {lightboxImage && (
        <div className="lightbox" onClick={closeLightbox}>
          <div className="max-w-4xl w-full mx-4 relative" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={closeLightbox}
              className="absolute -top-12 right-0 text-white text-3xl hover:text-orange-400 transition-colors"
            >
              <i className="fas fa-times"></i>
            </button>
            {photoItems.length > 1 && (
              <button
                type="button"
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-3xl bg-black bg-opacity-40 hover:bg-opacity-65 w-12 h-12 rounded-full flex items-center justify-center transition-all z-20"
              >
                <i className="fas fa-chevron-left"></i>
              </button>
            )}
            <img
              src={lightboxImage.src || '/logo.jpg'}
              alt={lightboxImage.title}
              className="w-full rounded-xl max-h-screen object-contain mx-auto"
              style={{ maxHeight: '85vh' }}
            />
            {photoItems.length > 1 && (
              <button
                type="button"
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-3xl bg-black bg-opacity-40 hover:bg-opacity-65 w-12 h-12 rounded-full flex items-center justify-center transition-all z-20"
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            )}
            {lightboxImage.title && (
              <p className="text-white text-center mt-3 text-sm font-medium">{lightboxImage.title}</p>
            )}
          </div>
        </div>
      )}

      {/* Video lightbox — same as public Gallery */}
      {playingVideo && (
        <div className="lightbox" onClick={closeVideo}>
          <div className="max-w-4xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white text-xl font-semibold">{playingVideo.title}</h3>
              <button type="button" onClick={closeVideo} className="text-white text-3xl hover:text-orange-400 transition-colors">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="rounded-xl overflow-hidden bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${playingVideo.youtubeId}?autoplay=1`}
                title={playingVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full rounded-xl"
                style={{ height: '50vh' }}
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryAdmin;
